import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, Text } from '@mantine/core';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import moment from 'moment';
import axios from 'axios';

import Layout from 'components/Layout/Layout';
import DesiredLoan from './DesiredLoan';
import PersonalInformation from './PersonalInformation';
import AddressContact from './AddressContact';
import SpouseInformation from './SpouseInformation';
import PrivacyPolicy from './PrivacyPolicy';
import { MARRIED, MID_DAY_TIME, MIN_DESIRED_LOAN, MIN_DESIRED_RATE, MIN_TIME, PHONE_NUMBER_LENGTH } from 'constants/App';
import { ContactForm } from './Contacts.types';
import * as Crypto from 'utils/Crypto';
import FormFooter from 'components/Layout/Footer/FormFooter';

export interface IPageData {
  form: UseFormReturnType<ContactForm>;
  isMobile?: boolean;
  t: any;
}

const initialValues = {
  desiredLoan: MIN_DESIRED_LOAN,
  desiredRate: MIN_DESIRED_RATE,
  fullName: '',
  phone: '',
  email: '',
  dateOfBirth: undefined,
  maritalStatus: '',
  zip: '',
  location: '',
  street: '',
  number: '',
  workdayFrom: MIN_TIME,
  workdayTo: MID_DAY_TIME,
  saturdayFrom: MIN_TIME,
  saturdayTo: MID_DAY_TIME,
  saturdayAvailability: false,
  spouseFullName: '',
  spousePhone: '',
  spouseEmail: '',
  spousePrivacyPolicy: false,
  privacyPolicy: false
};

const ContactPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 780px)');
  const [disabled, setDisabled] = useState<boolean>(false);

  const form = useForm({
    initialValues,
    onValuesChange: (values, previous) => {
      if (previous.maritalStatus === 'MARRIED' && values.maritalStatus !== previous.maritalStatus) {
        values.spouseFullName = '';
        values.spousePhone = '';
        values.spouseEmail = '';
        values.spousePrivacyPolicy = false;
      }
    }
  });

  const submitApplication = async (values: any) => {
    if (!isFormValid(values)) {
      return;
    }
    setDisabled(true);
    let dateOfBirth = values.dateOfBirth;
    if (dateOfBirth) {
      dateOfBirth = moment(dateOfBirth).format('YYYY-MM-DD');
    }
    const data = {
      desiredLoan: values.desiredLoan,
      desiredRate: values.desiredRate,
      fullName: values.fullName,
      phone: values.phone,
      email: values.email,
      dateOfBirth: dateOfBirth,
      maritalStatus: values.maritalStatus,
      zip: values.zip,
      city: values.location,
      street: values.street,
      number: values.number,
      workdayFrom: values.workdayFrom,
      workdayTo: values.workdayTo,
      saturdayFrom: values.saturdayFrom,
      saturdayTo: values.saturdayTo,
      partnerFullName: values.spouseFullName,
      partnerPhone: values.spousePhone,
      partnerEmail: values.spouseEmail,
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/v1/web/email-disclaimer`, Crypto.encrypt(data), {
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': 'en',
        }
      });
      if (response.status === 200) {
        form.reset();
        navigate('/success');
      } else {
        handleEmailError();
      }
    } catch (err) {
      handleEmailError();
    }
  }

  const handleEmailError = () => {
    setDisabled(false);
    notifications.show({
      title: t('notification.error.title'),
      message: t('notification.error.message'),
      position: 'top-right',
      color: 'red'
    });
  };

  const isFormValid = (values: any) => {
    const validations: {
      field: string;
      condition: boolean;
      error: string
    }[] = [
      { field: 'desiredLoan', condition: !values.desiredLoan || values.desiredLoan <= 0, error: t('input.required') },
      { field: 'desiredRate', condition: !values.desiredRate || values.desiredRate <= 0, error: t('input.required') },
      { field: 'fullName', condition: !values.fullName, error: t('input.required') },
      { field: 'phone', condition: !values.phone, error: t('input.required') },
      { field: 'phone', condition: values.phone && values.phone.replace(/\s/g, "").length !== PHONE_NUMBER_LENGTH, error: t('input.phone.invalid') },
      { field: 'email', condition: !values.email, error: t('input.required') },
      { field: 'email', condition: values.email && !/^\S+@\S+$/.test(values.email), error: t('input.invalid') },
      { field: 'dateOfBirth', condition: !values.dateOfBirth, error: t('input.required') },
      { field: 'maritalStatus', condition: !values.maritalStatus, error: t('input.required') },
      { field: 'zip', condition: !values.zip, error: t('input.required') },
      { field: 'location', condition: !values.location, error: t('input.required') },
      { field: 'street', condition: !values.street, error: t('input.required') },
      { field: 'number', condition: !values.number, error: t('input.required') },
      { field: 'workdayFrom', condition: !values.workdayFrom, error: t('input.required') },
      { field: 'workdayTo', condition: !values.workdayTo, error: t('input.required') },
      { field: 'privacyPolicy', condition: !values.privacyPolicy, error: t('input.required') },
    ];

    if (values.maritalStatus === MARRIED) {
      validations.push(
        { field: 'spousePrivacyPolicy', condition: !values.spousePrivacyPolicy, error: t('input.required') },
        { field: 'spouseFullName', condition: !values.spouseFullName, error: t('input.required') },
        { field: 'spousePhone', condition: !values.spousePhone, error: t('input.required') },
        { field: 'spousePhone', condition: values.spousePhone === values.phone, error: t('input.phone.spouse.invalid') },
        { field: 'spousePhone', condition: values.spousePhone && values.spousePhone.replace(/\s/g, "").length !== PHONE_NUMBER_LENGTH, error: t('input.phone.invalid') },
        { field: 'spouseEmail', condition: !values.spouseEmail, error: t('input.required') },
        { field: 'spouseEmail', condition: values.spouseEmail && !/^\S+@\S+$/.test(values.spouseEmail), error: t('input.invalid') },
        { field: 'spouseEmail', condition: values.spouseEmail === values.email, error: t('input.email.spouse.invalid') }
      );
    }

    let isValid = true;
    validations.forEach(({ field, condition, error }) => {
      if (condition) {
        form.setFieldError(field, error);
        isValid = false;
      }
    });
    return isValid;
  }

  return (
    <Layout t={t} title={t('header.title.form')} isMobile={isMobile}>
      <form onSubmit={form.onSubmit((values) => submitApplication(values))}>
        <DesiredLoan t={t} form={form} isMobile={isMobile} />
        <PersonalInformation t={t} form={form} isMobile={isMobile} />
        <AddressContact t={t} form={form} isMobile={isMobile} />
        <SpouseInformation t={t} form={form} isMobile={isMobile} />
        <PrivacyPolicy t={t} form={form} isMobile={isMobile} />
        <Flex align={'center'} direction={'column'} mt={40}>
          <Text mb={10} style={{ textAlign: 'center' }} >{t('application.confirm')}</Text>
          <Button
            disabled={disabled}
            w={'40%'}
            h={40}
            radius={10}
            bg={'#62AF45'}
            type='submit'
          >
            {t('button.send')}
          </Button>
        </Flex>
        <FormFooter t={t} />
      </form>
    </Layout>
  )
}

export default ContactPage;